import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { useSelector } from 'react-redux';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowUpRightIcon, 
  ArrowDownRightIcon, 
  UsersIcon,
  DocumentTextIcon,
  UserPlusIcon,
  DocumentArrowDownIcon,
  ChatBubbleOvalLeftEllipsisIcon,
  ArchiveBoxIcon,
  EnvelopeIcon,
  PlusIcon,
  ArrowDownTrayIcon,
  LightBulbIcon,
  ChartBarIcon,
  DocumentArrowDownIcon as DownloadIcon,
  PencilSquareIcon
} from '@heroicons/react/24/outline';
import { getDashboardAnalyticsApi, exportDashboardReportApi, saveTargetSnapshotApi } from '../../api/dashboardApi';
import { customerApi } from '../../api/customerApi';
import { paymentApi } from '../../api/paymentApi';
import { generateTaxInvoice } from '../../utils/generateInvoice';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  BarChart,
  Bar
} from 'recharts';

const Dashboard = () => {
  const { user } = useSelector((state) => state.auth);
  const { activeBusiness } = useAuth();
  const navigate = useNavigate();

  const [stats, setStats] = useState({
    youWillGive: 0,
    youWillGet: 0,
    netBalance: 0,
    netProfit: 0,
    gstEstimate: 0,
    monthlyTarget: 0,
    targetProgress: 0
  });
  
  const [trendData, setTrendData] = useState([]);
  const [topDefaulters, setTopDefaulters] = useState([]);
  const [aiInsights, setAiInsights] = useState([]);
  const [targetHistory, setTargetHistory] = useState([]);
  const [hrStats, setHrStats] = useState({ present: 0, absent: 0, halfDay: 0, totalStaff: 0, estimatedPayroll: 0, warningEmailsSent: 0 });
  const [inventoryStats, setInventoryStats] = useState({ totalItems: 0, totalValue: 0, lowStockCount: 0, lowStockItems: [] });
  const [recentInvoices, setRecentInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const handleWhatsAppReminder = async (e, customer) => {
    e.stopPropagation();
    try {
      const toastId = toast.loading('Generating payment link and logging reminder...');
      
      // Call createPaymentOrder to generate an actual payment link
      let paymentLink = '';
      try {
        const payRes = await paymentApi.createPaymentOrder({
          customerId: customer.id,
          amount: customer.balance
        });
        if (payRes.data.success && payRes.data.data.paymentLink) {
          paymentLink = payRes.data.data.paymentLink;
        }
      } catch (err) {
        console.warn("Could not generate actual payment link, using mock fallback", err);
        paymentLink = `http://localhost:5173/pay/mock_${Math.random().toString(36).substring(7)}`;
      }

      await customerApi.sendReminder(customer.id);
      
      const message = `Hello ${customer.name},\nThis is a gentle reminder regarding your pending balance of Rs. ${customer.balance}.\n${paymentLink ? `Please process the payment via this link: ${paymentLink}` : 'Please process the payment at your earliest convenience.'}\n\nThank you!`;
      const whatsappUrl = `https://wa.me/91${customer.phone}?text=${encodeURIComponent(message)}`;
      window.open(whatsappUrl, '_blank');
      
      toast.success('Reminder sent on WhatsApp!', { id: toastId });
    } catch (error) {
      console.error('Failed to send reminder', error);
      toast.error('Could not log reminder');
    }
  };

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  const fetchDashboardStats = async () => {
    try {
      setLoading(true);
      const res = await getDashboardAnalyticsApi();
      const data = res.data.data;
      
      setStats({
        youWillGive: data.stats.totalGave || 0,
        youWillGet: data.stats.totalGot || 0,
        netBalance: data.stats.netBalance || 0,
        netProfit: data.stats.netProfit || 0,
        gstEstimate: data.stats.gstEstimate || 0,
        monthlyTarget: data.stats.monthlyTarget || 0,
        targetProgress: data.stats.targetProgress || 0
      });
      setTrendData(data.trendData || []);
      setTargetHistory(data.targetHistory || []);
      setTopDefaulters(data.topDefaulters || []);
      setAiInsights(data.aiInsights || []);
      if (data.hrStats) setHrStats(data.hrStats);
      if (data.inventoryStats) setInventoryStats(data.inventoryStats);
      if (data.recentInvoices) setRecentInvoices(data.recentInvoices);

    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadTarget = () => {
    if (targetHistory.length === 0) {
      alert('No target history available to download');
      return;
    }
    
    let csvContent = 'Month,Goal Set,Achieved,Progress (%)\n';
    targetHistory.forEach(th => {
      const progress = th.target > 0 ? ((th.achieved / th.target) * 100).toFixed(2) : 0;
      csvContent += `${th.month},${th.target},${th.achieved},${progress}%\n`;
    });

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'Monthly_Target_History.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleExportReport = async () => {
    try {
      const res = await exportDashboardReportApi();
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'CA_Report.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Export failed', error);
      alert('Failed to export report');
    }
  };

  const handleSaveSnapshot = async () => {
    try {
      await saveTargetSnapshotApi();
      fetchDashboardStats(); // Refresh data
    } catch (error) {
      console.error('Save snapshot failed', error);
    }
  };

  const totalTransactionVolume = stats.youWillGet + stats.youWillGive;
  const getPercentage = (value) => totalTransactionVolume === 0 ? 0 : Math.round((value / totalTransactionVolume) * 100);

  const pieData = [
    { name: 'You Will Get (Income)', value: stats.youWillGet },
    { name: 'You Will Give (Expense)', value: stats.youWillGive },
  ];
  const COLORS = ['#22c55e', '#ef4444'];

  const hrPieData = [
    { name: 'Present', value: hrStats.present },
    { name: 'Absent', value: hrStats.absent },
    { name: 'Half Day', value: hrStats.halfDay }
  ];
  const HR_COLORS = ['#22c55e', '#ef4444', '#eab308'];

  const handleDownloadInvoice = (invoice) => {
    generateTaxInvoice(
      {
        invoiceNumber: invoice.invoiceNumber,
        items: invoice.items,
        subtotal: invoice.subtotal,
        taxAmount: invoice.taxAmount,
        discountAmount: invoice.discountAmount,
        advanceAmount: invoice.paidAmount,
        balanceDue: invoice.balanceDue,
        grandTotal: invoice.totalAmount
      },
      activeBusiness,
      invoice.customerId || invoice.walkInCustomer || { name: 'Walk-in Customer' },
      invoice.taxAmount > 0,
      activeBusiness?.settings?.gstRate || 18
    );
  };

  if (loading) {
    return (
      <div className="w-full h-96 flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#dc343b]"></div>
      </div>
    );
  }

  return (
    <div className="w-full space-y-6 pb-10 max-w-7xl mx-auto">
      
      {/* Header Section */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-2">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white tracking-tight">
            Hello, {user?.name?.split(' ')[0] || 'User'}
          </h1>
          <p className="text-[14px] text-gray-500 dark:text-gray-400 mt-1">Here is the financial summary for <strong className="text-gray-700 dark:text-gray-300">{user?.business?.businessName}</strong>.</p>
        </div>
        <div className="flex flex-wrap gap-3 mt-4 sm:mt-0">
          <button 
            onClick={() => navigate('/create-invoice')}
            className="flex items-center gap-2 bg-purple-600 text-white px-4 py-2.5 rounded-xl font-bold text-[13px] hover:bg-purple-700 transition-colors shadow-sm shadow-purple-500/20"
          >
            <DocumentTextIcon className="h-4 w-4" />
            Generate Invoice
          </button>
          <button 
            onClick={handleExportReport}
            className="flex items-center gap-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 px-4 py-2.5 rounded-xl font-bold text-[13px] hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors shadow-sm"
          >
            <ArrowDownTrayIcon className="h-4 w-4" />
            Export CA Report
          </button>
          <button 
            onClick={() => navigate('/customers/add')}
            className="flex items-center gap-2 bg-[#dc343b] text-white px-4 py-2.5 rounded-xl font-bold text-[13px] hover:bg-[#b91c1c] transition-colors shadow-sm shadow-red-500/20"
          >
            <UserPlusIcon className="h-4 w-4" />
            Add Customer
          </button>
        </div>
      </div>

      {/* Primary KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-5">
        
        {/* Net Profit / Loss */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 border border-gray-100 dark:border-gray-700 shadow-[0_2px_10px_-4px_rgba(0,0,0,0.05)] transition-colors relative overflow-hidden group">
          <div className={`absolute -right-6 -top-6 w-24 h-24 rounded-full blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 ${stats.netProfit >= 0 ? 'bg-green-500/20' : 'bg-red-500/20'}`}></div>
          <div className="flex items-center gap-3 mb-2 relative z-10">
            <div className={`p-2 rounded-lg ${stats.netProfit >= 0 ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'}`}>
              <ChartBarIcon className="h-5 w-5" />
            </div>
            <h3 className="text-[13px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Net Profit</h3>
          </div>
          <p className="text-[13px] font-medium text-gray-400 dark:text-gray-500 mb-1 relative z-10">
            Actual earnings this month
          </p>
          <h2 className={`text-3xl font-extrabold mt-3 relative z-10 ${stats.netProfit >= 0 ? 'text-green-600 dark:text-green-500' : 'text-red-600 dark:text-red-500'}`}>
            {stats.netProfit >= 0 ? '+' : '-'}₹{Math.abs(stats.netProfit).toLocaleString()}
          </h2>
        </div>

        {/* Net Balance Card */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 border border-gray-100 dark:border-gray-700 shadow-[0_2px_10px_-4px_rgba(0,0,0,0.05)] transition-colors">
          <div className="flex items-center gap-3 mb-2">
            <div className={`p-2 rounded-lg ${stats.netBalance >= 0 ? 'bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400' : 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400'}`}>
              {stats.netBalance >= 0 ? <ArrowDownRightIcon className="h-5 w-5" /> : <ArrowUpRightIcon className="h-5 w-5" />}
            </div>
            <h3 className="text-[13px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Net Balance</h3>
          </div>
          <p className="text-[13px] font-medium text-gray-400 dark:text-gray-500 mb-1">
            Overall business standing
          </p>
          <h2 className={`text-3xl font-extrabold mt-3 ${stats.netBalance >= 0 ? 'text-green-600 dark:text-green-500' : 'text-red-600 dark:text-red-500'}`}>
            {stats.netBalance >= 0 ? '+' : '-'}₹{Math.abs(stats.netBalance).toLocaleString()}
          </h2>
        </div>

        {/* You Will Get */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 border border-gray-100 dark:border-gray-700 shadow-[0_2px_10px_-4px_rgba(0,0,0,0.05)] relative overflow-hidden group transition-colors">
          <div className="absolute -right-6 -top-6 w-24 h-24 bg-green-50 dark:bg-green-900/20 rounded-full blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
          <div className="relative z-10">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-[13px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">You Will Get</h3>
              <span className="text-[11px] font-bold bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 px-2 py-0.5 rounded-md">{getPercentage(stats.youWillGet)}% of volume</span>
            </div>
            <p className="text-[13px] font-medium text-gray-400 dark:text-gray-500 mb-1">Money owed to you</p>
            <h2 className="text-3xl font-extrabold text-gray-900 dark:text-white mt-3">₹{stats.youWillGet.toLocaleString()}</h2>
            
            {/* Mini Progress Bar */}
            <div className="w-full bg-gray-100 dark:bg-gray-700 rounded-full h-1.5 mt-5 overflow-hidden">
              <div className="bg-green-500 h-1.5 rounded-full" style={{ width: `${getPercentage(stats.youWillGet)}%` }}></div>
            </div>
          </div>
        </div>

        {/* You Will Give */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 border border-gray-100 dark:border-gray-700 shadow-[0_2px_10px_-4px_rgba(0,0,0,0.05)] relative overflow-hidden group transition-colors">
          <div className="absolute -right-6 -top-6 w-24 h-24 bg-red-50 dark:bg-red-900/20 rounded-full blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
          <div className="relative z-10">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-[13px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">You Will Give</h3>
              <span className="text-[11px] font-bold bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 px-2 py-0.5 rounded-md">{getPercentage(stats.youWillGive)}% of volume</span>
            </div>
            <p className="text-[13px] font-medium text-gray-400 dark:text-gray-500 mb-1">Money you owe</p>
            <h2 className="text-3xl font-extrabold text-gray-900 dark:text-white mt-3">₹{stats.youWillGive.toLocaleString()}</h2>
            
            {/* Mini Progress Bar */}
            <div className="w-full bg-gray-100 dark:bg-gray-700 rounded-full h-1.5 mt-5 overflow-hidden">
              <div className="bg-red-500 h-1.5 rounded-full" style={{ width: `${getPercentage(stats.youWillGive)}%` }}></div>
            </div>
          </div>
        </div>

        {/* Inventory Value */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 border border-gray-100 dark:border-gray-700 shadow-[0_2px_10px_-4px_rgba(0,0,0,0.05)] relative overflow-hidden group transition-colors">
          <div className="absolute -right-6 -top-6 w-24 h-24 bg-blue-50 dark:bg-blue-900/20 rounded-full blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
          <div className="relative z-10">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 rounded-lg">
                <ArchiveBoxIcon className="h-5 w-5" />
              </div>
              <h3 className="text-[13px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Godown Value</h3>
            </div>
            <p className="text-[13px] font-medium text-gray-400 dark:text-gray-500 mb-1">Total value of current stock</p>
            <h2 className="text-3xl font-extrabold text-blue-600 dark:text-blue-500 mt-3">₹{inventoryStats.totalValue.toLocaleString()}</h2>
          </div>
        </div>

        {/* Staff & Payroll */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 border border-gray-100 dark:border-gray-700 shadow-[0_2px_10px_-4px_rgba(0,0,0,0.05)] relative overflow-hidden group transition-colors">
          <div className="absolute -right-6 -top-6 w-24 h-24 bg-purple-50 dark:bg-purple-900/20 rounded-full blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
          <div className="relative z-10">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400 rounded-lg">
                <UsersIcon className="h-5 w-5" />
              </div>
              <h3 className="text-[13px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Est. Payroll</h3>
            </div>
            <p className="text-[13px] font-medium text-gray-400 dark:text-gray-500 mb-1">{hrStats.totalStaff} Active Staff</p>
            <h2 className="text-3xl font-extrabold text-purple-600 dark:text-purple-500 mt-3">₹{hrStats.estimatedPayroll.toLocaleString()}</h2>
          </div>
        </div>

      </div>

      {/* Lower Section Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 pt-2">
        
        {/* Main Content Area (Feature List & Charts) */}
        <div className="lg:col-span-2 space-y-6">

          {/* AI Smart Insights */}
          {aiInsights.length > 0 && (
            <div className="bg-gradient-to-br from-[#0f172a] to-[#1e293b] rounded-2xl p-6 shadow-lg border border-slate-700 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-500/10 rounded-full blur-3xl"></div>
              <div className="flex items-center gap-3 mb-4 relative z-10">
                <div className="p-2 bg-indigo-500/20 text-indigo-400 rounded-lg">
                  <LightBulbIcon className="h-5 w-5" />
                </div>
                <h2 className="text-[16px] font-bold text-white">Smart Insights</h2>
              </div>
              <div className="space-y-3 relative z-10">
                {aiInsights.map((insight, idx) => (
                  <div key={idx} className="flex items-start gap-3 bg-slate-800/50 p-4 rounded-xl border border-slate-700">
                    <div className={`mt-0.5 w-2 h-2 rounded-full flex-shrink-0 ${
                      insight.type === 'danger' ? 'bg-red-500 shadow-[0_0_8px_rgba(239,68,68,0.8)]' :
                      insight.type === 'warning' || insight.type === 'alert' ? 'bg-amber-500 shadow-[0_0_8px_rgba(245,158,11,0.8)]' :
                      insight.type === 'success' ? 'bg-green-500 shadow-[0_0_8px_rgba(34,197,94,0.8)]' :
                      'bg-blue-500 shadow-[0_0_8px_rgba(59,130,246,0.8)]'
                    }`}></div>
                    <p className="text-[13px] font-medium text-slate-300 leading-relaxed">{insight.message}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Charts Section */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-[0_2px_10px_-4px_rgba(0,0,0,0.05)] p-6 transition-colors">
            <h2 className="text-[16px] font-bold text-gray-900 dark:text-white mb-6">Cashflow Trend (Last 30 Days)</h2>
            <div className="h-72 w-full">
              {trendData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={trendData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                    <XAxis dataKey="date" tick={{fontSize: 12, fill: '#94a3b8'}} axisLine={false} tickLine={false} />
                    <YAxis tick={{fontSize: 12, fill: '#94a3b8'}} axisLine={false} tickLine={false} tickFormatter={(val) => `₹${val}`} />
                    <Tooltip 
                      contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)' }}
                      labelStyle={{ fontWeight: 'bold', color: '#1e293b', marginBottom: '4px' }}
                    />
                    <Legend iconType="circle" wrapperStyle={{ fontSize: '13px', paddingTop: '10px' }} />
                    <Line type="monotone" name="Incoming (Got)" dataKey="got" stroke="#22c55e" strokeWidth={3} dot={false} activeDot={{ r: 6 }} />
                    <Line type="monotone" name="Outgoing (Gave)" dataKey="gave" stroke="#ef4444" strokeWidth={3} dot={false} activeDot={{ r: 6 }} />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-gray-400 font-medium text-[14px]">
                  No transaction data available yet.
                </div>
              )}
            </div>
          </div>

          {/* 12-Month Target vs Achieved Chart */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-[0_2px_10px_-4px_rgba(0,0,0,0.05)] p-6 transition-colors">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-[16px] font-bold text-gray-900 dark:text-white">Monthly Target vs Achieved</h2>
              <div className="flex gap-2">
                <button 
                  onClick={handleDownloadTarget}
                  className="flex items-center gap-1 text-[12px] font-bold bg-gray-50 text-gray-600 hover:bg-gray-100 px-3 py-1.5 rounded-lg border border-gray-200 transition-colors"
                  title="Download CSV"
                >
                  <ArrowDownTrayIcon className="h-3 w-3" />
                  Download
                </button>
                <button 
                  onClick={handleSaveSnapshot}
                  className="text-[12px] font-bold bg-blue-50 text-blue-600 hover:bg-blue-100 px-3 py-1.5 rounded-lg border border-blue-200 transition-colors"
                >
                  Save This Month
                </button>
              </div>
            </div>
            <div className="h-72 w-full">
              {targetHistory.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={targetHistory} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                    <XAxis dataKey="month" tick={{fontSize: 12, fill: '#94a3b8'}} axisLine={false} tickLine={false} />
                    <YAxis tick={{fontSize: 12, fill: '#94a3b8'}} axisLine={false} tickLine={false} tickFormatter={(val) => `₹${val}`} />
                    <Tooltip 
                      cursor={{ fill: 'transparent' }}
                      contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)' }}
                      labelStyle={{ fontWeight: 'bold', color: '#1e293b', marginBottom: '4px' }}
                    />
                    <Legend iconType="circle" wrapperStyle={{ fontSize: '13px', paddingTop: '10px' }} />
                    <Bar dataKey="target" name="Goal Set" fill="#cbd5e1" radius={[4, 4, 0, 0]} barSize={20} />
                    <Bar dataKey="achieved" name="Achieved" fill="#3b82f6" radius={[4, 4, 0, 0]} barSize={20} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-gray-400 font-medium text-[14px]">
                  No target history available yet. Click "Save This Month" to generate data.
                </div>
              )}
            </div>
          </div>
          
          {/* Recent Invoices List */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-[0_2px_10px_-4px_rgba(0,0,0,0.05)] p-6 transition-colors md:col-span-2">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-[16px] font-bold text-gray-900 dark:text-white">Recent Invoices</h2>
                <p className="text-[13px] text-gray-500 dark:text-gray-400 mt-1">Quickly view and download your latest bills.</p>
              </div>
            </div>

            {recentInvoices.length === 0 ? (
              <div className="text-center py-10 bg-gray-50 dark:bg-gray-800/50 rounded-xl border border-dashed border-gray-200 dark:border-gray-700">
                <DocumentTextIcon className="h-10 w-10 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                <p className="text-[14px] font-bold text-gray-600 dark:text-gray-400">No invoices yet!</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="border-b border-gray-100 dark:border-gray-700">
                      <th className="pb-3 text-[12px] font-bold text-gray-400 uppercase tracking-wider">Invoice #</th>
                      <th className="pb-3 text-[12px] font-bold text-gray-400 uppercase tracking-wider">Customer</th>
                      <th className="pb-3 text-[12px] font-bold text-gray-400 uppercase tracking-wider">Date</th>
                      <th className="pb-3 text-[12px] font-bold text-gray-400 uppercase tracking-wider">Amount</th>
                      <th className="pb-3 text-[12px] font-bold text-gray-400 uppercase tracking-wider text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentInvoices.map((inv) => (
                      <tr key={inv._id} className="border-b border-gray-50 dark:border-gray-800 last:border-0 hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
                        <td className="py-4 text-[14px] font-bold text-gray-900 dark:text-white">{inv.invoiceNumber}</td>
                        <td className="py-4 text-[14px] text-gray-700 dark:text-gray-300">
                          {inv.customerId ? inv.customerId.name : (inv.walkInCustomer?.name || 'Walk-in Customer')}
                        </td>
                        <td className="py-4 text-[13px] text-gray-500 dark:text-gray-400">
                          {new Date(inv.issueDate).toLocaleDateString('en-IN')}
                        </td>
                        <td className="py-4 text-[14px] font-extrabold text-[#dc343b] dark:text-red-400">
                          ₹{inv.totalAmount.toLocaleString()}
                        </td>
                        <td className="py-4 text-right flex items-center justify-end gap-2">
                          <button 
                            onClick={() => navigate(`/edit-invoice/${inv._id}`)}
                            className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg text-[12px] font-bold transition-colors"
                          >
                            <PencilSquareIcon className="w-4 h-4" /> Edit
                          </button>
                          <button 
                            onClick={() => handleDownloadInvoice(inv)}
                            className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 bg-[#dc343b]/10 text-[#dc343b] hover:bg-[#dc343b] hover:text-white rounded-lg text-[12px] font-bold transition-colors"
                          >
                            <DownloadIcon className="w-4 h-4" /> Download
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-[0_2px_10px_-4px_rgba(0,0,0,0.05)] p-6 transition-colors">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-[16px] font-bold text-gray-900 dark:text-white">Priority Customers to Collect</h2>
                <p className="text-[13px] text-gray-500 dark:text-gray-400 mt-1">Customers with the highest outstanding balances.</p>
              </div>
              <button onClick={() => navigate('/customers')} className="text-[12px] font-bold text-[#dc343b] dark:text-red-400 hover:text-[#b91c1c] uppercase tracking-wider transition-colors">
                View All
              </button>
            </div>

            {topDefaulters.length === 0 ? (
              <div className="text-center py-10 bg-gray-50 dark:bg-gray-800/50 rounded-xl border border-dashed border-gray-200 dark:border-gray-700">
                <UsersIcon className="h-10 w-10 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                <p className="text-[14px] font-bold text-gray-600 dark:text-gray-400">No pending collections!</p>
                <p className="text-[12px] text-gray-400 dark:text-gray-500 mt-1">Your ledger is completely settled.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {topDefaulters.map((customer, idx) => (
                  <div key={idx} className="flex items-center justify-between p-4 rounded-xl border border-gray-100 dark:border-gray-700 hover:shadow-md transition-shadow bg-white dark:bg-gray-800/50">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center text-gray-700 dark:text-gray-300 font-bold text-[16px]">
                        {customer.name.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <h4 className="text-[15px] font-bold text-gray-900 dark:text-white">{customer.name}</h4>
                        <p className="text-[12px] font-medium text-gray-500 dark:text-gray-400 mt-0.5">{customer.phone}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-6">
                      <div className="text-right">
                        <p className="text-[11px] font-bold text-gray-400 uppercase tracking-widest mb-0.5">Owes You</p>
                        <p className="text-[16px] font-extrabold text-green-600">₹{customer.balance.toLocaleString()}</p>
                      </div>
                      <button 
                        onClick={(e) => handleWhatsAppReminder(e, customer)}
                        className="hidden sm:flex items-center justify-center h-10 w-10 rounded-full bg-green-50 text-green-600 hover:bg-green-500 hover:text-white transition-all shadow-sm"
                        title="Send WhatsApp Reminder"
                      >
                        <ChatBubbleOvalLeftEllipsisIcon className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Low Stock Alerts */}
          {inventoryStats.lowStockCount > 0 && (
            <div className="bg-red-50 dark:bg-red-900/10 rounded-2xl border border-red-100 dark:border-red-900/30 shadow-sm p-6 transition-colors">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-red-100 dark:bg-red-900/50 text-red-600 dark:text-red-400 rounded-lg animate-pulse">
                    <ArchiveBoxIcon className="h-5 w-5" />
                  </div>
                  <div>
                    <h2 className="text-[16px] font-bold text-red-900 dark:text-red-400">Low Stock Alerts</h2>
                    <p className="text-[13px] text-red-700 dark:text-red-300 mt-0.5">{inventoryStats.lowStockCount} items need restocking</p>
                  </div>
                </div>
                <button onClick={() => navigate('/inventory')} className="text-[12px] font-bold text-red-700 dark:text-red-400 hover:text-red-900 bg-white/50 px-3 py-1.5 rounded-lg border border-red-200">
                  Manage
                </button>
              </div>

              <div className="space-y-3">
                {inventoryStats.lowStockItems.map((item, idx) => (
                  <div key={idx} className="flex justify-between items-center bg-white/60 dark:bg-gray-800/60 p-3 rounded-xl border border-red-100 dark:border-red-900/20">
                    <span className="font-bold text-[14px] text-gray-900 dark:text-white">{item.name}</span>
                    <div className="text-right">
                      <span className="text-[12px] font-bold text-red-600 bg-red-100 px-2 py-0.5 rounded-md">Stock: {item.currentStock}</span>
                      <p className="text-[10px] font-medium text-gray-500 mt-1">Min required: {item.minStockLevel}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          
        </div>

        {/* Features Sidebar */}
        <div className="space-y-6">

          {/* Monthly Sales Target */}
          {stats.monthlyTarget > 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-[0_2px_10px_-4px_rgba(0,0,0,0.05)] p-5 transition-colors">
              <div className="flex justify-between items-center mb-1">
                <h3 className="text-[12px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest">Monthly Target</h3>
                <span className="text-[12px] font-bold text-gray-900 dark:text-white">{Math.min(100, Math.round(stats.targetProgress))}%</span>
              </div>
              <p className="text-[10px] text-gray-400 font-medium mb-3">Goal: ₹{stats.monthlyTarget.toLocaleString()}</p>
              <div className="w-full bg-gray-100 dark:bg-gray-700 rounded-full h-2 overflow-hidden">
                <div 
                  className={`h-2 rounded-full transition-all duration-1000 ${stats.targetProgress >= 100 ? 'bg-green-500' : 'bg-blue-500'}`} 
                  style={{ width: `${Math.min(100, stats.targetProgress)}%` }}
                ></div>
              </div>
            </div>
          )}

          {/* GST Estimate */}
          <div className="bg-gradient-to-r from-orange-50 to-amber-50 dark:from-orange-900/10 dark:to-amber-900/10 rounded-2xl border border-orange-100 dark:border-orange-900/20 shadow-sm p-5 transition-colors flex items-center justify-between">
            <div>
              <h3 className="text-[12px] font-bold text-orange-600 dark:text-orange-400 uppercase tracking-widest">Est. GST</h3>
              <p className="text-[11px] text-orange-500/80 font-medium mt-0.5">Based on total sales</p>
            </div>
            <h2 className="text-xl font-extrabold text-orange-600 dark:text-orange-500">
              ₹{Math.round(stats.gstEstimate).toLocaleString()}
            </h2>
          </div>

          {/* Today's Attendance Pie Chart */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-[0_2px_10px_-4px_rgba(0,0,0,0.05)] p-5 transition-colors">
            <div className="flex justify-between items-center mb-2">
              <h3 className="text-[12px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest">Today's Attendance</h3>
              <span className="text-[10px] font-bold bg-purple-50 text-purple-600 px-2 py-0.5 rounded-full">{hrStats.totalStaff} Staff</span>
            </div>
            <div className="h-48 w-full relative">
              {hrStats.present > 0 || hrStats.absent > 0 || hrStats.halfDay > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={hrPieData.filter(d => d.value > 0)}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={70}
                      paddingAngle={5}
                      dataKey="value"
                      stroke="none"
                    >
                      {hrPieData.filter(d => d.value > 0).map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={HR_COLORS[hrPieData.findIndex(d => d.name === entry.name)]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-gray-400 font-medium text-[13px] text-center px-4">
                  No attendance marked for today
                </div>
              )}
            </div>
            <div className="mt-2 flex items-center justify-between bg-red-50 p-2 rounded-xl border border-red-100">
              <span className="text-[12px] font-bold text-red-600 flex items-center gap-1"><EnvelopeIcon className="h-4 w-4"/> Emails Sent</span>
              <span className="text-[14px] font-extrabold text-red-700">{hrStats.warningEmailsSent}</span>
            </div>
          </div>
          
          {/* Income vs Expense Pie Chart */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-[0_2px_10px_-4px_rgba(0,0,0,0.05)] p-5 transition-colors">
            <h3 className="text-[12px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-2">Income vs Expense</h3>
            <div className="h-48 w-full relative">
              {totalTransactionVolume > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={70}
                      paddingAngle={5}
                      dataKey="value"
                      stroke="none"
                    >
                      {pieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => `₹${value}`} />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-gray-400 font-medium text-[13px] text-center px-4">
                  Add transactions to see breakdown
                </div>
              )}
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-[0_2px_10px_-4px_rgba(0,0,0,0.05)] p-5 transition-colors">
            <h3 className="text-[12px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-4">Quick Links</h3>
            <ul className="space-y-2">
              <li>
                <button 
                  onClick={() => navigate('/customers')}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700/50 text-gray-700 dark:text-gray-300 transition-colors group"
                >
                  <UsersIcon className="h-5 w-5 text-gray-400 dark:text-gray-500 group-hover:text-[#dc343b] dark:group-hover:text-red-400 transition-colors" />
                  <span className="text-[14px] font-bold">Manage Customers</span>
                </button>
              </li>
              <li>
                <button className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700/50 text-gray-700 dark:text-gray-300 transition-colors group">
                  <DocumentTextIcon className="h-5 w-5 text-gray-400 dark:text-gray-500 group-hover:text-[#dc343b] dark:group-hover:text-red-400 transition-colors" />
                  <span className="text-[14px] font-bold">Generate Reports</span>
                </button>
              </li>
            </ul>
          </div>

          <div className="bg-[#0f172a] rounded-2xl p-6 relative overflow-hidden group">
            <div className="absolute -right-6 -bottom-6 w-32 h-32 bg-[#dc343b] rounded-full blur-2xl opacity-30 group-hover:opacity-40 transition-opacity"></div>
            <div className="relative z-10">
              <span className="inline-block px-2.5 py-1 bg-white/10 text-white text-[10px] font-bold uppercase tracking-widest rounded-md mb-4 backdrop-blur-sm">
                Pro Feature
              </span>
              <h3 className="text-[16px] font-bold text-white mb-2">Automate SMS Reminders</h3>
              <p className="text-[13px] font-medium text-slate-400 leading-relaxed mb-5">
                Automatically send payment links to customers via SMS or WhatsApp before the due date.
              </p>
              <button className="w-full bg-[#dc343b] text-white px-4 py-2.5 rounded-xl font-bold text-[13px] hover:bg-[#b91c1c] transition-colors shadow-lg shadow-red-500/20">
                Setup Automation
              </button>
            </div>
          </div>

        </div>
      </div>

      {/* Floating Action Button (FAB) */}
      <div className="fixed bottom-6 right-6 group z-50">
        <div className="absolute bottom-full right-0 mb-3 hidden group-hover:flex flex-col items-end gap-2">
          <button onClick={() => navigate('/customers/add')} className="flex items-center gap-3 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 px-4 py-2 rounded-xl shadow-lg border border-gray-100 dark:border-gray-700 font-bold text-[13px] hover:text-[#dc343b] transition-colors whitespace-nowrap">
            Add Customer
          </button>
          <button onClick={() => navigate('/inventory')} className="flex items-center gap-3 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 px-4 py-2 rounded-xl shadow-lg border border-gray-100 dark:border-gray-700 font-bold text-[13px] hover:text-[#dc343b] transition-colors whitespace-nowrap">
            Add Inventory
          </button>
          <button onClick={() => navigate('/staff')} className="flex items-center gap-3 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 px-4 py-2 rounded-xl shadow-lg border border-gray-100 dark:border-gray-700 font-bold text-[13px] hover:text-[#dc343b] transition-colors whitespace-nowrap">
            Mark Attendance
          </button>
        </div>
        <button className="flex items-center justify-center h-14 w-14 rounded-full bg-[#dc343b] text-white shadow-xl shadow-red-500/30 hover:bg-[#b91c1c] transition-all transform group-hover:scale-110 group-hover:rotate-45">
          <PlusIcon className="h-6 w-6" />
        </button>
      </div>

    </div>
  );
};

export default Dashboard;
